import { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, LineChart, Line } from 'recharts';
import { sourceColors, durumColors, type Lead } from '../../../mocks/leads';

const TOOLTIP_STYLE = { background: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 11 };

function StatCard({ label, value, icon, accent, sub }: { label: string; value: number | string; icon: string; accent: string; sub?: string }) {
  return (
    <div className="relative rounded-xl bg-white/[0.03] border border-white/[0.07] p-5 flex flex-col gap-3 overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 blur-2xl" style={{ background: accent }} />
      <div className="flex items-center justify-between">
        <span className="text-white/40 text-xs font-medium uppercase tracking-widest">{label}</span>
        <span className="w-9 h-9 flex items-center justify-center rounded-lg text-base" style={{ background: accent + '22', color: accent }}>
          <i className={icon} />
        </span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-white/30 text-xs">{sub}</p>}
    </div>
  );
}

interface DashboardProps {
  leads: Lead[];
  onViewDetail: (lead: Lead) => void;
  onOpenDrawer: (lead: Lead) => void;
}

export default function Dashboard({ leads, onViewDetail, onOpenDrawer }: DashboardProps) {
  const today = new Date().toISOString().slice(0, 10);

  const stats = useMemo(() => ({
    total: leads.length,
    bugun: leads.filter((l) => l.olusturma_tarihi.startsWith(today)).length,
    katilmak: leads.filter((l) => l.lansmanKatilim).length,
    davet: leads.filter((l) => l.davetGonderildi).length,
    manuel: leads.filter((l) => l.kaynak === 'Manuel').length,
    excel: leads.filter((l) => l.kaynak === 'Excel').length,
    whatsapp: leads.filter((l) => l.kaynak === 'WhatsApp').length,
    satinAldi: leads.filter((l) => l.durum === 'Satın Aldı').length,
  }), [leads, today]);

  const sourceData = useMemo(() => {
    const c: Record<string, number> = {};
    leads.forEach((l) => { c[l.kaynak] = (c[l.kaynak] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name, value }));
  }, [leads]);

  const durumData = useMemo(() => {
    const c: Record<string, number> = {};
    leads.forEach((l) => { c[l.durum] = (c[l.durum] || 0) + 1; });
    return Object.entries(c).map(([name, value]) => ({ name, value }));
  }, [leads]);

  const dailyTrend = useMemo(() => {
    const c: Record<string, number> = {};
    leads.forEach((l) => { const d = l.olusturma_tarihi.slice(0, 10); c[d] = (c[d] || 0) + 1; });
    return Object.entries(c).sort().slice(-7).map(([d, v]) => ({ gun: d.slice(5), basvuru: v }));
  }, [leads]);

  const funnel = [
    { label: 'Başvuru', count: leads.length, color: '#f59e0b' },
    { label: 'Davet', count: stats.davet, color: '#3b82f6' },
    { label: 'Katılım', count: leads.filter((l) => l.lansmanKatilim).length, color: '#10b981' },
    { label: 'Satış', count: stats.satinAldi, color: '#e1306c' },
  ];
  const funnelMax = funnel[0]?.count || 1;

  const recent = [...leads].sort((a, b) => b.olusturma_tarihi.localeCompare(a.olusturma_tarihi)).slice(0, 6);

  const partRate = leads.length ? Math.round((leads.filter((l) => l.lansmanKatilim).length / leads.length) * 100) : 0;
  const inviteRate = leads.length ? Math.round((stats.davet / leads.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-white">Dashboard</h1>
        <p className="text-white/30 text-sm mt-0.5">Franchise Lansman Operasyon Paneli</p>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Toplam Başvuru" value={stats.total} icon="ri-group-line" accent="#f59e0b" sub="Tüm kanallar" />
        <StatCard label="Bugünkü Başvuru" value={stats.bugun} icon="ri-calendar-check-line" accent="#10b981" sub="Bugün" />
        <StatCard label="Katılmak İsteyenler" value={stats.katilmak} icon="ri-rocket-2-line" accent="#3b82f6" sub="Lansman talebi" />
        <StatCard label="Satın Aldı" value={stats.satinAldi} icon="ri-vip-crown-line" accent="#f59e0b" sub="Dönüşüm" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Davet Gönderilenler" value={stats.davet} icon="ri-mail-send-line" accent="#8b5cf6" />
        <StatCard label="WhatsApp Kaynaklı" value={stats.whatsapp} icon="ri-whatsapp-line" accent="#25d366" />
        <StatCard label="Katılım Oranı" value={partRate + '%'} icon="ri-pie-chart-line" accent="#10b981" />
        <StatCard label="Davet Oranı" value={inviteRate + '%'} icon="ri-send-plane-2-line" accent="#60a5fa" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Source Pie */}
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Kaynak Dağılımı</p>
          <ResponsiveContainer width="100%" height={170}>
            <PieChart>
              <Pie data={sourceData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                {sourceData.map((e, i) => <Cell key={i} fill={sourceColors[e.name as keyof typeof sourceColors] || '#6b7280'} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 mt-2">
            {sourceData.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: sourceColors[s.name as keyof typeof sourceColors] || '#6b7280' }} />
                <span className="text-white/40 text-[11px]">{s.name} <span className="text-white/60 font-semibold">{s.value}</span></span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Trend */}
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Günlük Trend (Son 7 Gün)</p>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={dailyTrend}>
              <XAxis dataKey="gun" tick={{ fill: '#ffffff40', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="basvuru" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Funnel */}
        <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-4">Dönüşüm Hunisi</p>
          <div className="flex flex-col gap-2 mt-2">
            {funnel.map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-white/40 text-xs w-20 shrink-0">{step.label}</span>
                <div className="flex-1 h-2 rounded-full bg-white/[0.05] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.round((step.count / funnelMax) * 100)}%`, background: step.color }} />
                </div>
                <span className="text-white font-semibold text-sm w-8 text-right">{step.count}</span>
              </div>
            ))}
          </div>
          {/* Durum Bar */}
          <p className="text-white/40 text-[10px] font-semibold uppercase tracking-widest mt-5 mb-3">Durum Dağılımı</p>
          <ResponsiveContainer width="100%" height={90}>
            <BarChart data={durumData} barSize={10}>
              <XAxis dataKey="name" tick={{ fill: '#ffffff30', fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {durumData.map((e, i) => <Cell key={i} fill={durumColors[e.name as keyof typeof durumColors] || '#6b7280'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Applicants */}
      <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <p className="text-white/70 text-sm font-semibold">Son Başvurular</p>
          <span className="text-white/30 text-xs">{leads.length} toplam</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                {['ID', 'Ad Soyad', 'Kaynak', 'Durum', 'Katılım', 'Tarih', ''].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-white/30 text-[11px] font-semibold uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map((lead) => (
                <tr key={lead.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => onOpenDrawer(lead)}>
                  <td className="px-5 py-3 text-white/25 text-xs font-mono">{lead.id}</td>
                  <td className="px-5 py-3 text-white font-medium text-sm">{lead.ad_soyad}</td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: (sourceColors[lead.kaynak] || '#6b7280') + '22', color: sourceColors[lead.kaynak] || '#6b7280' }}>
                      {lead.kaynak}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: (durumColors[lead.durum] || '#94a3b8') + '22', color: durumColors[lead.durum] || '#94a3b8' }}>
                      {lead.durum}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {lead.lansmanKatilim
                      ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Katılacak</span>
                      : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/[0.06] text-white/30">Bekleniyor</span>}
                  </td>
                  <td className="px-5 py-3 text-white/25 text-xs">{new Date(lead.olusturma_tarihi).toLocaleDateString('tr-TR')}</td>
                  <td className="px-5 py-3">
                    <button onClick={(e) => { e.stopPropagation(); onOpenDrawer(lead); }}
                      className="text-amber-400 hover:text-amber-300 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap">
                      Aç
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
